import { useEffect, useRef, useState } from 'react';

/**
 * `m:ss` while under an hour, `h:mm:ss` past it — minutes must roll into hours or a
 * multi-hour payment deadline reads as "117:12", which no one parses as two hours.
 * Hours are not capped at a day: a digits-only `d:hh:mm:ss` is less readable than
 * the hours simply accumulating, and units would need translating.
 */
const formatCountdown = (totalSeconds: number) => {
  const pad = (value: number) => value.toString().padStart(2, '0');
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}:${pad(minutes)}:${pad(seconds)}`;
  }
  return `${minutes}:${pad(seconds)}`;
};

export const getReservationRemainingMs = (reservedUntil: string | null | undefined): number | null => {
  if (!reservedUntil) {
    return null;
  }
  return new Date(reservedUntil).getTime() - Date.now();
};

export const getReservationCountdownDisplay = (
  reservedUntil: string | null | undefined,
  options?: { expiredLabel?: string }
): string | null => {
  if (!reservedUntil) {
    return null;
  }

  const remainingMs = getReservationRemainingMs(reservedUntil) ?? 0;
  const remainingSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
  if (remainingSeconds <= 0) {
    return options?.expiredLabel ?? null;
  }

  return formatCountdown(remainingSeconds);
};

/**
 * Share of the hold still left, 0-1, for a countdown bar. `null` when the hold's start is
 * unknown — a bar that cannot say how much of the TTL is gone is worse than no bar.
 */
export const getReservationRemainingFraction = (
  reservedFrom: string | null | undefined,
  reservedUntil: string | null | undefined
): number | null => {
  if (!reservedFrom || !reservedUntil) {
    return null;
  }

  const totalMs = new Date(reservedUntil).getTime() - new Date(reservedFrom).getTime();
  if (totalMs <= 0) {
    return null;
  }

  const remainingMs = getReservationRemainingMs(reservedUntil) ?? 0;
  return Math.min(1, Math.max(0, remainingMs / totalMs));
};

type UseReservationCountdownOptions = {
  expiredLabel?: string;
  /** Fires once when the reservation reaches or passes `reservedUntil` (including on mount if already expired). */
  onExpired?: () => void;
};

/** Live `m:ss` (or `h:mm:ss`) countdown until `reservedUntil`. Re-renders every second while active. */
export const useReservationCountdown = (
  reservedUntil: string | null | undefined,
  options?: UseReservationCountdownOptions
): string | null => {
  const [, forceRender] = useState(0);
  const onExpired = options?.onExpired;
  const hasFiredExpiredRef = useRef(false);

  useEffect(() => {
    hasFiredExpiredRef.current = false;
  }, [reservedUntil]);

  useEffect(() => {
    if (!reservedUntil) {
      return;
    }

    const fireExpiredIfNeeded = () => {
      const remainingMs = getReservationRemainingMs(reservedUntil);
      if (remainingMs == null || remainingMs > 0) {
        return false;
      }
      if (!hasFiredExpiredRef.current) {
        hasFiredExpiredRef.current = true;
        onExpired?.();
      }
      return true;
    };

    if (fireExpiredIfNeeded()) {
      return;
    }

    const interval = setInterval(() => {
      forceRender((n) => n + 1);
      fireExpiredIfNeeded();
    }, 1000);

    return () => clearInterval(interval);
  }, [reservedUntil, onExpired]);

  return getReservationCountdownDisplay(reservedUntil, options);
};
